import React, { useState, useEffect } from 'react';
import { apiService, regionManager } from '../services/api';
import { PlatformRegion } from '../types';
import WPMLogo from '../components/WPMLogo';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [regions, setRegions] = useState<PlatformRegion[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(true);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionsData = await apiService.getRegions();
        setRegions(regionsData);
        if (regionsData.length > 0) {
          setSelectedRegion(regionsData[0].id.toString());
        }
      } catch {
        setError('Failed to load regions. Please refresh the page.');
      } finally {
        setIsLoadingRegions(false);
      }
    };

    fetchRegions();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRegion) {
      setError('Please select a region');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const region = regions.find(r => r.id.toString() === selectedRegion);
      if (region) {
        regionManager.setRegion(region.name);
      }

      await apiService.login(formData.username, formData.password);
      window.location.href = '/';
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Login failed';
      setError(errorMsg);
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!selectedRegion) {
      setError('Please select a region first');
      return;
    }

    setIsMicrosoftLoading(true);
    setError(null);

    try {
      const region = regions.find(r => r.id.toString() === selectedRegion);
      if (region) {
        regionManager.setRegion(region.name);
      }

      const response = await apiService.getMicrosoftLoginUrl();
      window.location.href = response.login_url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initiate Microsoft login');
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-purple-800 p-5">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl p-10 shadow-2xl">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <WPMLogo size={56} />
            </div>
            <h2 className="m-0 mb-1 text-slate-800 text-3xl font-bold tracking-wide">WPM</h2>
            <p className="m-0 text-gray-400 text-sm">Widget Portal Management</p>
            <p className="m-0 text-gray-500 text-sm mt-3">Sign in to your account</p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-800 px-4 py-3 rounded-md border border-red-200 mb-5 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label htmlFor="region" className="block mb-2 font-semibold text-slate-800 text-sm">Region</label>
              <select
                id="region"
                name="region"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                required
                disabled={isLoading || isLoadingRegions}
                className="w-full px-4 py-3 border border-gray-200 rounded-md text-base transition-all focus:outline-none focus:border-purple-600 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.2)] disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {isLoadingRegions ? (
                  <option value="">Loading regions...</option>
                ) : regions.length === 0 ? (
                  <option value="">No regions available</option>
                ) : (
                  <>
                    <option value="">Select a region</option>
                    {regions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name} - {region.environment}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div className="mb-5">
              <label htmlFor="username" className="block mb-2 font-semibold text-slate-800 text-sm">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                placeholder="Enter your username"
                autoComplete="username"
                className="w-full px-4 py-3 border border-gray-200 rounded-md text-base transition-all focus:outline-none focus:border-purple-600 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.2)] disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="password" className="block mb-2 font-semibold text-slate-800 text-sm">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                placeholder="Enter your password"
                autoComplete="current-password"
                className="w-full px-4 py-3 border border-gray-200 rounded-md text-base transition-all focus:outline-none focus:border-purple-600 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.2)] disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>

            <button
              type="submit"
              className="w-full px-5 py-3.5 bg-gradient-to-br from-purple-600 to-purple-800 text-white border-0 rounded-md text-base font-semibold cursor-pointer transition-all mt-2.5 hover:translate-y-[-2px] hover:shadow-[0_4px_12px_rgba(147,51,234,0.4)] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading || isLoadingRegions || !selectedRegion}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={isMicrosoftLoading || isLoadingRegions || !selectedRegion}
              className="w-full px-5 py-3.5 bg-white text-gray-700 border-2 border-gray-300 rounded-md text-base font-semibold cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              {isMicrosoftLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
